import React from "react";
import { Box, Button, Chip, IconButton, MenuItem, Tab, Tabs, TextField, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { enqueueSnackbar } from "notistack";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { FormDialog, PageContainer, SectionHeader } from "../../Layout/Common";
import { subscribePoll } from "../../Layout/Poller";
import JobPanel from "./JobPanel";
import StringsTab from "./StringsTab";
import CoverageTab from "./CoverageTab";
import ContextTab from "./ContextTab";
import ImportExportTab from "./ImportExportTab";
import { isActive } from "./loc";

// The texts of a title on the backend (the Localization service): every tag with its languages and tiers, the AI
// jobs that translate and review them, the project context the AI reads, and the xlsx round trip.
// URL: /<game>/localization[/<tab>[/<folder path>]]; tabs strings | coverage | context | import | jobs.
const TABS = [["strings", "Strings"], ["coverage", "Coverage"], ["context", "Project context"], ["import", "Import / Export"], ["jobs", "Jobs"]];
const loc = api.group("localization");

export default class LocalizationPage extends React.Component {
    state = { tab: "strings", tree: null, project: null, jobs: null, loading: false, deepFolder: null, deepTag: null,
        // "Publish to a version": the per-language files a client version downloads (`localization publish <branch> <M.m>`)
        publishOpen: false, publishBranch: "Development", publishVersion: "", publishing: false, published: null };
    lastJobsPoll = 0;

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const tab = TABS.some(([k]) => k === deep[0]) ? deep[0] : "strings";
        // the rest of the path is the folder (nested folders carry slashes: news/items/Production/<key>)
        this.setState({ tab, deepFolder: deep.length > 1 ? deep.slice(1).join("/") : null, deepTag: null }, () => { this.load(); this.loadJobs(true); });
        this.unsub = subscribePoll(this.pollJobs, 5000);
    }

    componentWillUnmount() { if (this.unsub) this.unsub(); }

    get canEdit() {
        if (globals.loginData?.titles?.isMaster) return true;
        const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
        const roles = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
        return roles.includes("localization");
    }

    load = () => {
        this.setState({ loading: true });
        return Promise.all([
            loc("Loading the tree", ["tree"], { silent: true }),
            loc("Loading the project", ["project"], { silent: true }),
        ]).then(([tree, project]) => {
            this.setState({ tree: typeof tree === "string" ? null : tree, project: typeof project === "string" ? null : project });
        }).catch(() => {}).finally(() => this.setState({ loading: false }));
    }

    // Every 5 s while a job runs, every 30 s otherwise (the list is a cheap read either way)
    pollJobs = () => {
        const active = (this.state.jobs?.jobs ?? []).some(isActive);
        if (!active && Date.now() - this.lastJobsPoll < 30000) return Promise.resolve(true);
        return this.loadJobs(false);
    }

    loadJobs = (first) => {
        this.lastJobsPoll = Date.now();
        return loc("Loading jobs", ["jobs"], { silent: true }).then(r => {
            if (typeof r === "string" || !r) return true;
            const wasActive = (this.state.jobs?.jobs ?? []).filter(isActive).map(j => j.id);
            this.setState({ jobs: r });
            // a job that just finished: refresh what the tabs show
            const nowActive = new Set((r.jobs ?? []).filter(isActive).map(j => j.id));
            if (!first && wasActive.some(id => !nowActive.has(id))) this.onDataChanged();
            return true;
        }).catch(() => true);
    }

    onDataChanged = () => { this.load(); this.tabRef?.reload?.(); }
    onJobsChanged = () => { this.lastJobsPoll = 0; this.loadJobs(false); }

    cancelJob = (id) => loc("Cancelling the job", ["translate", "cancel", id]).then(r => { if (typeof r === "string") enqueueSnackbar(r, { variant: "error" }); this.onJobsChanged(); }).catch(() => {});

    // What a version's clients download right now (patch, languages); read when the dialog's branch/version changes
    readPublished = () => {
        const { publishBranch, publishVersion } = this.state;
        if (!/^\d+\.\d+$/.test(publishVersion.trim())) { this.setState({ published: null }); return; }
        loc("Reading the published texts", ["published", publishBranch, publishVersion.trim()], { silent: true })
            .then(r => this.setState({ published: typeof r === "string" ? r : r }))
            .catch(() => this.setState({ published: null }));
    }

    publish = () => {
        const { publishBranch, publishVersion } = this.state;
        this.setState({ publishing: true });
        loc(`Publishing ${publishBranch} ${publishVersion.trim()}`, ["publish", publishBranch, publishVersion.trim()], { timeout: 120000, retry: false })
            .then(r => {
                if (typeof r === "string") enqueueSnackbar(r, { variant: r.startsWith("Publish failed") || r.startsWith("Usage") ? "error" : "info" });
                else enqueueSnackbar(`Published ${publishBranch} ${publishVersion.trim()}: patch ${r?.patch ?? "?"}${r?.tags != null ? `, ${r.tags} tags` : ""}${r?.languages ? `, ${Array.isArray(r.languages) ? r.languages.length : r.languages} languages` : ""}`, { variant: "success" });
                this.setState({ publishOpen: false });
            })
            .catch(() => {})
            .finally(() => this.setState({ publishing: false }));
    }

    renderPublish() {
        const { publishOpen, publishBranch, publishVersion, publishing, published } = this.state;
        const valid = /^\d+\.\d+$/.test(publishVersion.trim());
        return (
            <FormDialog open={publishOpen} title="Publish the texts to a client version" onClose={() => this.setState({ publishOpen: false })}
                onConfirm={this.publish} confirmText={publishing ? "Publishing…" : "Publish"} confirmDisabled={!valid || publishing} cancelDisabled={publishing}>
                <Typography variant="body2" sx={{ mb: 2, maxWidth: 520 }}>
                    Writes every language's file for this branch and major.minor to the CDN. The clients of that version pick
                    the new texts at their next boot. New or edited tags reach nobody until they are published.
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField select size="small" label="Branch" value={publishBranch} sx={{ width: 180 }}
                        onChange={e => this.setState({ publishBranch: e.target.value, published: null }, this.readPublished)}>
                        {["Development", "Internal", "Beta", "Production"].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Version (major.minor)" placeholder="0.23" value={publishVersion} sx={{ width: 180 }}
                        onChange={e => this.setState({ publishVersion: e.target.value, published: null })} onBlur={this.readPublished} />
                </Box>
                {published && typeof published === "object" && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.75 }}>
                        Published now: patch {published.patch ?? "none"}{published.languages ? ` · ${Array.isArray(published.languages) ? published.languages.length : published.languages} languages` : ""}
                    </Typography>
                )}
            </FormDialog>
        );
    }

    setTab = (_, tab) => { this.setState({ tab, deepFolder: null, deepTag: null }); replaceDeep([tab]); }
    onFolderChange = (folder) => replaceDeep([this.state.tab, folder ?? ""]);

    render() {
        const { tab, tree, project, jobs, loading, deepFolder, deepTag } = this.state;
        const canEdit = this.canEdit;
        const common = { loc, tree, project, canEdit, reloadTree: this.load, onJobsChanged: this.onJobsChanged, ref: r => { this.tabRef = r; } };
        const running = (jobs?.jobs ?? []).filter(isActive).length;
        return (
            <PageContainer maxWidth={1600}>
                <SectionHeader title="Localization">
                    {tree && <Chip size="small" label={`${tree.folders.reduce((n, f) => n + f.tags, 0)} tags · ${tree.languages.length} languages`} />}
                    {running > 0 && <Chip size="small" color="info" label={`${running} job${running > 1 ? "s" : ""} running`} onClick={() => this.setTab(null, "jobs")} />}
                    {!canEdit && <Chip size="small" label="read only" />}
                    {canEdit && <Button size="small" variant="outlined" onClick={() => this.setState({ publishOpen: true }, this.readPublished)}>Publish to a version…</Button>}
                    <IconButton size="small" onClick={this.onDataChanged} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                </SectionHeader>
                {this.renderPublish()}
                <Tabs value={tab} onChange={this.setTab} sx={{ mb: 2 }}>
                    {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
                {tab === "jobs" && <JobPanel standalone jobs={jobs} canEdit={canEdit} onCancel={this.cancelJob} />}
                {tab !== "jobs" && (!tree || !project ? <Typography color="text.secondary">{loading ? "Loading…" : "The title has no texts yet."}</Typography> : (
                    <Box>
                        {tab === "strings" && <StringsTab key="strings" {...common} initialFolder={deepFolder} initialTag={deepTag} onFolderChange={this.onFolderChange} />}
                        {tab === "coverage" && <CoverageTab key="coverage" {...common} initialFolder={deepFolder} onFolderChange={this.onFolderChange} />}
                        {tab === "context" && <ContextTab key="context" {...common} onProjectChanged={this.load} />}
                        {tab === "import" && <ImportExportTab key="import" {...common} onImported={this.onDataChanged} />}
                    </Box>
                ))}
            </PageContainer>
        );
    }
}
